import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, LayoutDashboard, Home, Palette, Scissors, Tag, Images, Clock, Building2, Settings, ExternalLink, LogOut, Menu, X, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WebsiteAdminAuthProvider, useWebsiteAdmin } from "../context/WebsiteAdminAuth";
import { apiError, cmsApi } from "../api";
import Overview from "./Overview";
import HomepageAdmin from "./HomepageAdmin";
import ServicesAdmin from "./ServicesAdmin";
import PromotionsAdmin from "./PromotionsAdmin";
import GalleryAdmin from "./GalleryAdmin";
import HoursAdmin from "./HoursAdmin";
import BusinessAdmin from "./BusinessAdmin";
import SettingsAdmin from "./SettingsAdmin";
import BrandingAdmin from "./BrandingAdmin";
import AppointmentsAdmin from "./AppointmentsAdmin";

const NAV = [
  { to: "", icon: LayoutDashboard, label: "Übersicht", end: true, id: "uebersicht" },
  { to: "startseite", icon: Home, label: "Startseite" },
  { to: "branding", icon: Palette, label: "Logo & Branding" },
  { to: "terminanfragen", icon: CalendarCheck, label: "Terminanfragen" },
  { to: "dienstleistungen", icon: Scissors, label: "Dienstleistungen" },
  { to: "angebote", icon: Tag, label: "Angebote" },
  { to: "galerie", icon: Images, label: "Galerie" },
  { to: "oeffnungszeiten", icon: Clock, label: "Öffnungszeiten" },
  { to: "geschaeft", icon: Building2, label: "Geschäftsinformationen" },
  { to: "einstellungen", icon: Settings, label: "SEO & Einstellungen" },
];

function Login() {
  const { login } = useWebsiteAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  useEffect(() => { cmsApi.get("/website-admin/auth/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: null })); }, []);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("Willkommen im Website-Admin"); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-stone-200/70 p-8" data-testid="website-admin-login-form">
        <div className="flex items-center gap-2 text-[#4A1D24]"><Sparkles className="h-5 w-5 text-[#C59B6C]" /><span className="text-xs tracking-[0.3em] font-semibold">HAIR BY SAMIRA</span></div>
        <h1 className="font-serif text-3xl mt-4 text-stone-900">Website-Admin</h1>
        <p className="text-sm text-stone-500 mt-1 mb-6">Inhalte Ihrer Website verwalten.</p>
        <div className="space-y-4">
          <div><Label className="text-xs uppercase tracking-wide text-stone-500">E-Mail</Label><Input data-testid="website-admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wide text-stone-500">Passwort</Label><Input data-testid="website-admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" /></div>
          <Button data-testid="website-admin-login-submit" type="submit" disabled={loading} className="w-full bg-[#4A1D24] hover:bg-[#3a161c] text-white">{loading ? "Anmelden…" : "Anmelden"}</Button>
        </div>
        {status?.configured && (
          <p className="text-xs text-stone-500 mt-5 rounded-lg bg-[#FAF7F2] border border-stone-200 px-3 py-2" data-testid="website-admin-login-hint">
            Konto: <span className="font-mono">{status.email_hint}</span> · Zugangsdaten werden sicher über die Server-Umgebungsvariablen <span className="font-mono">{status.credential_source === "WEBSITE_ADMIN_EMAIL" ? "WEBSITE_ADMIN_EMAIL / WEBSITE_ADMIN_PASSWORD" : "ADMIN_EMAIL / ADMIN_PASSWORD"}</span> konfiguriert. Das Passwort können Sie nach dem Login unter „SEO &amp; Einstellungen“ ändern.
          </p>
        )}
        {status && status.configured === false && (
          <p className="text-xs text-red-700 mt-5 rounded-lg bg-red-50 border border-red-200 px-3 py-2" data-testid="website-admin-not-configured">
            Noch kein Website-Admin-Konto angelegt. Bitte <span className="font-mono">WEBSITE_ADMIN_EMAIL</span> und <span className="font-mono">WEBSITE_ADMIN_PASSWORD</span> in den Server-Secrets setzen und den Server neu starten.
          </p>
        )}
        <p className="text-xs text-stone-400 mt-6 text-center"><Link to="/" className="hover:underline">Zur Website</Link> · <Link to="/admin" className="hover:underline">Loyalty-Admin</Link></p>
      </form>
    </div>
  );
}

function Sidebar({ onNavigate }) {
  const { admin, logout } = useWebsiteAdmin();
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 flex items-center gap-2 text-stone-50"><Sparkles className="h-5 w-5 text-[#C59B6C]" /><div><p className="text-[10px] tracking-[0.3em] font-semibold text-[#E7C79A]">HAIR BY SAMIRA</p><p className="font-serif text-lg leading-tight">Website-Admin</p></div></div>
      <nav className="flex-1 px-3 space-y-0.5" data-testid="website-admin-sidebar">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to ? `/website-admin/${n.to}` : "/website-admin"} end={n.end} onClick={onNavigate} data-testid={`sidebar-${n.id || n.to}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-white/10 text-white font-semibold" : "text-stone-300 hover:bg-white/5 hover:text-white"}`}>
            <n.icon className="h-4 w-4" />{n.label}
          </NavLink>
        ))}
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#E7C79A] hover:bg-white/5 mt-2" data-testid="sidebar-view-site"><ExternalLink className="h-4 w-4" />Website ansehen</a>
      </nav>
      <div className="px-5 py-4 border-t border-white/10 text-xs text-stone-400">
        <p className="truncate" data-testid="website-admin-user">{admin?.email}</p>
        <button onClick={logout} className="inline-flex items-center gap-1.5 mt-2 text-stone-300 hover:text-white" data-testid="website-admin-logout"><LogOut className="h-3.5 w-3.5" /> Abmelden</button>
      </div>
    </div>
  );
}

function Shell() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-stone-900 lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block bg-[#2d1015] sticky top-0 h-screen"><Sidebar /></aside>
      <div className="lg:hidden sticky top-0 z-30 bg-[#2d1015] text-stone-50 flex items-center justify-between px-4 h-14">
        <span className="font-serif text-lg">Website-Admin</span>
        <button onClick={() => setOpen(true)} aria-label="Menü" data-testid="website-admin-menu-toggle"><Menu className="h-6 w-6" /></button>
      </div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#2d1015] shadow-2xl"><button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-stone-300" aria-label="Schließen"><X className="h-5 w-5" /></button><Sidebar onNavigate={() => setOpen(false)} /></aside>
        </div>
      )}
      <main className="px-4 sm:px-8 py-8 max-w-6xl w-full mx-auto">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="startseite" element={<HomepageAdmin />} />
          <Route path="branding" element={<BrandingAdmin />} />
          <Route path="terminanfragen" element={<AppointmentsAdmin />} />
          <Route path="dienstleistungen" element={<ServicesAdmin />} />
          <Route path="angebote" element={<PromotionsAdmin />} />
          <Route path="galerie" element={<GalleryAdmin />} />
          <Route path="oeffnungszeiten" element={<HoursAdmin />} />
          <Route path="geschaeft" element={<BusinessAdmin />} />
          <Route path="einstellungen" element={<SettingsAdmin />} />
          <Route path="*" element={<Navigate to="/website-admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Gate() {
  const { admin } = useWebsiteAdmin();
  if (admin === null) return <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#4A1D24] border-t-transparent animate-spin" /></div>;
  if (!admin) return <Login />;
  return <Shell />;
}

export default function WebsiteAdminApp() {
  return <WebsiteAdminAuthProvider><Gate /></WebsiteAdminAuthProvider>;
}
